import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Package, Activity } from 'lucide-react';
import { collection, getCountFromServer, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Loader } from '@/components/ui/loader';
import { Card } from '@/components/ui/card';
import { labelClassName } from '@/components/ui/label';
import { Table, TableHead, TableBody, TableRow, TableHeadCell, TableCell } from '@/components/ui/table';
import { fmtRelative, statusColor, STATUS_KEY } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import type { AuditDoc } from '@/types/models';

export default function AdminHome() {
  const { t, locale } = useT();
  const [stats, setStats] = useState<{ orgs: number; audits: number; releases: number } | null>(null);
  const [recent, setRecent] = useState<AuditDoc[]>([]);

  useEffect(() => {
    (async () => {
      const [orgs, audits, releases] = await Promise.all([
        getCountFromServer(collection(db, 'organizations')),
        getCountFromServer(collection(db, 'audits')),
        getCountFromServer(collection(db, 'releases')),
      ]);
      setStats({ orgs: orgs.data().count, audits: audits.data().count, releases: releases.data().count });

      const q = query(collection(db, 'audits'), orderBy('createdAt', 'desc'), limit(10));
      const snap = await getDocs(q);
      setRecent(snap.docs.map(d => ({ id: d.id, ...d.data() } as AuditDoc)));
    })();
  }, []);

  if (!stats) return <Loader fullscreen />;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl text-ink-900 dark:text-white">{t('admin.home.title')}</h1>
        <p className="mt-1 text-sm text-ink-500">{t('admin.home.subtitle')}</p>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat icon={<Building2 className="h-4 w-4" />} label={t('admin.home.kpi.orgs')}     value={stats.orgs}     to="/admin/orgs" />
        <Stat icon={<Activity className="h-4 w-4" />}  label={t('admin.home.kpi.audits')}   value={stats.audits} />
        <Stat icon={<Package className="h-4 w-4" />}   label={t('admin.home.kpi.releases')} value={stats.releases} to="/admin/releases" />
      </div>

      <section>
        <h2 className="mb-3 font-display text-lg text-ink-900 dark:text-white">{t('admin.home.recent')}</h2>
        <Table>
          <TableHead>
            <tr>
              <TableHeadCell>{t('admin.home.col.audit')}</TableHeadCell>
              <TableHeadCell>{t('admin.home.col.org')}</TableHeadCell>
              <TableHeadCell>{t('common.status')}</TableHeadCell>
              <TableHeadCell>{t('common.date')}</TableHeadCell>
            </tr>
          </TableHead>
          <TableBody>
            {recent.map(a => (
              <TableRow key={a.id}>
                <TableCell><Link className="font-medium text-ink-900 hover:text-brand-600 dark:text-white" to={`/audits/${a.id}`}>{a.displayName}</Link></TableCell>
                <TableCell className="text-ink-500"><Link to={`/admin/orgs/${a.orgId}`} className="hover:text-brand-600">{a.orgId}</Link></TableCell>
                <TableCell><span className={statusColor(a.status)}>{t(STATUS_KEY[a.status])}</span></TableCell>
                <TableCell className="text-ink-500">{fmtRelative(a.createdAt, t, locale)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>
    </div>
  );
}

function Stat({ icon, label, value, to }: { icon: React.ReactNode; label: string; value: number; to?: string }) {
  const inner = (
    <Card className="flex items-center justify-between">
      <div>
        <div className={labelClassName()}>{label}</div>
        <div className="mt-1 font-display text-3xl text-ink-900 dark:text-white">{value}</div>
      </div>
      <div className="grid h-9 w-9 place-items-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300">{icon}</div>
    </Card>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
}
